import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    ArrowLeftOutlined,
    DeleteOutlined,
    LineChartOutlined,
    SafetyCertificateOutlined
} from "@ant-design/icons";

import {
    useParams
} from "react-router-dom";

import {
    canSyncHealthData,
    getHealthHistory,
    saveHealthData,
    deleteHealthData
} from "../../api/healthTracking";

import {
    findExtraHealthTool
} from "./extraTools";


const makeLocalKey = (type) =>
    `healthExtra_${type}_history`;


const makeDate = () => {

    const now = new Date();

    return {
        date: now.toLocaleDateString(
            "vi-VN"
        ),
        time: now.toLocaleTimeString(
            "vi-VN",
            {
                hour: "2-digit",
                minute: "2-digit"
            }
        )
    };
};


const formatValue = (
    tool,
    data
) => {

    if (!tool || !data) {
        return "";
    }


    switch (tool.type) {

        case "can_nang_bmi": {

            const weight =
                Number(data.weight);

            const height =
                Number(data.height);

            let bmi = null;

            if (
                weight > 0 &&
                height > 0
            ) {

                const h =
                    height / 100;

                bmi =
                    weight /
                    (h * h);
            }

            return (
                `${data.weight || "-"} kg` +
                (
                    bmi
                        ? ` · BMI ${bmi.toFixed(1)}`
                        : ""
                )
            );
        }


        case "nhiet_do":
            return data.temperature
                ? `${data.temperature} °C`
                : "Đã lưu";


        case "nhip_tho":
            return data.respiratoryRate
                ? `${data.respiratoryRate} lần/phút`
                : "Đã lưu";


        case "vong_eo":
            return data.waist
                ? `${data.waist} cm`
                : "Đã lưu";


        case "giac_ngu":
            return data.sleepHours !== undefined
                ? `${data.sleepHours} giờ`
                : "Đã lưu";


        case "luong_nuoc":
            return data.water !== undefined
                ? `${data.water} mL`
                : "Đã lưu";


        case "trieu_chung":
            return (
                data.symptom ||
                "Đã lưu triệu chứng"
            );


        case "van_dong":
            return (
                data.activity ||
                "Đã lưu hoạt động"
            );


        case "buoc_chan":
            return data.steps !== undefined
                ? `${data.steps} bước`
                : "Đã lưu";


        case "ghi_chu_suc_khoe":
            return (
                data.content ||
                "Đã lưu ghi chú"
            );


        default:
            return "Đã lưu";
    }
};


const formatExtra = (
    tool,
    data
) => {

    switch (tool.type) {

        case "nhiet_do":
            return data.method &&
                data.method !== "Không ghi"
                ? data.method
                : "";


        case "giac_ngu":
            return data.quality || "";


        case "trieu_chung":
            return data.severity
                ? `Mức độ: ${data.severity}`
                : "";


        case "van_dong":
            return data.duration !== undefined &&
                data.duration !== ""
                ? `${data.duration} phút`
                : "";


        default:
            return "";
    }
};


export default function ExtraHealthTracker() {

    const { slug } =
        useParams();

    const tool =
        findExtraHealthTool(slug);

    const syncWithAccount =
        canSyncHealthData();

    const [form, setForm] =
        useState({});

    const [history, setHistory] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);


    const localKey =
        tool
            ? makeLocalKey(
                tool.type
            )
            : "";


    const loadHistory =
        async () => {

            if (!tool) {
                return;
            }

            setLoading(true);


            if (syncWithAccount) {

                const result =
                    await getHealthHistory(
                        tool.type
                    );

                if (result.success) {

                    setHistory(
                        (result.data || []).map(
                            (item) => ({
                                id:
                                    item.health_tracking_id,

                                ...(
                                    item.tracking_data ||
                                    {}
                                ),

                                created_at:
                                    item.created_at
                            })
                        )
                    );

                } else {

                    setHistory([]);
                }

            } else {

                try {

                    const saved =
                        JSON.parse(
                            localStorage.getItem(
                                localKey
                            ) || "[]"
                        );

                    setHistory(
                        Array.isArray(saved)
                            ? saved
                            : []
                    );

                } catch (error) {

                    setHistory([]);
                }
            }


            setLoading(false);
        };


    useEffect(() => {

        if (tool) {
            loadHistory();
        }

        // eslint-disable-next-line
    }, [slug]);


    const resultPreview =
        useMemo(
            () =>
                tool
                    ? formatValue(
                        tool,
                        form
                    )
                    : "",
            [tool, form]
        );


    if (!tool) {

        return (

            <div className="max-w-screen-xl mx-auto px-4 pt-20">

                <div className="bg-white border rounded-2xl p-8 text-center">

                    <p className="font-bold">
                        Không tìm thấy tiện ích
                    </p>

                    <a
                        href="/tienich/suc-khoe"
                        className="text-blue-600 font-semibold inline-block mt-3"
                    >
                        Quay lại Theo dõi sức khỏe
                    </a>

                </div>

            </div>
        );
    }


    const updateField = (
        key,
        value
    ) => {

        setForm(
            (current) => ({
                ...current,
                [key]: value
            })
        );
    };


    const validate = () => {

        for (
            const field
            of tool.fields
        ) {

            const value =
                form[field.key];

            if (
                value === undefined ||
                value === null ||
                String(value).trim() === ""
            ) {

                alert(
                    `Vui lòng nhập ${field.label.toLowerCase()}.`
                );

                return false;
            }


            if (
                field.type === "number"
            ) {

                const number =
                    Number(value);

                if (
                    !Number.isFinite(number)
                ) {

                    alert(
                        `${field.label} không hợp lệ.`
                    );

                    return false;
                }


                if (
                    field.min !== undefined &&
                    number < field.min
                ) {

                    alert(
                        `${field.label} không hợp lệ.`
                    );

                    return false;
                }


                if (
                    field.max !== undefined &&
                    number > field.max
                ) {

                    alert(
                        `${field.label} không hợp lệ.`
                    );

                    return false;
                }
            }
        }

        return true;
    };


    const handleSave =
        async () => {

            if (!validate()) {
                return;
            }


            const now =
                makeDate();

            const cleanData = {
                ...form,
                date: now.date,
                time: now.time
            };


            tool.fields.forEach(
                (field) => {

                    if (
                        field.type ===
                        "number"
                    ) {

                        cleanData[field.key] =
                            Number(
                                cleanData[
                                    field.key
                                ]
                            );
                    }
                }
            );


            if (syncWithAccount) {

                setSaving(true);

                const result =
                    await saveHealthData(
                        tool.type,
                        cleanData
                    );

                if (!result.success) {

                    setSaving(false);

                    alert(
                        "Không thể lưu dữ liệu. Vui lòng thử lại."
                    );

                    return;
                }


                await loadHistory();

                setSaving(false);

            } else {

                const record = {
                    id:
                        Date.now(),

                    ...cleanData
                };


                const next = [
                    record,
                    ...history
                ];


                setHistory(next);

                localStorage.setItem(
                    localKey,
                    JSON.stringify(next)
                );
            }


            setForm({});
        };


    const handleDelete =
        async (id) => {

            if (
                !window.confirm(
                    "Xóa bản ghi này?"
                )
            ) {
                return;
            }


            if (syncWithAccount) {

                const result =
                    await deleteHealthData(
                        id
                    );

                if (!result.success) {

                    alert(
                        "Không thể xóa dữ liệu."
                    );

                    return;
                }


                await loadHistory();

            } else {

                const next =
                    history.filter(
                        (item) =>
                            item.id !== id
                    );

                setHistory(next);

                localStorage.setItem(
                    localKey,
                    JSON.stringify(next)
                );
            }
        };


    return (

        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">


            <a
                href="/tienich/suc-khoe"
                className="inline-flex items-center text-blue-600 font-semibold text-sm mb-4"
            >

                <ArrowLeftOutlined className="mr-2" />

                Theo dõi sức khỏe

            </a>


            <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 rounded-2xl px-5 py-6 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-2xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">

                        {tool.icon}

                    </div>


                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">

                            THEO DÕI SỨC KHỎE

                        </p>


                        <h1 className="text-[25px] md:text-3xl font-bold">

                            {tool.name}

                        </h1>

                    </div>

                </div>


                <p className="text-sm md:text-base mt-4 opacity-90 leading-6 max-w-2xl">

                    {tool.description}

                </p>

            </div>


            <div
                className={
                    syncWithAccount
                        ? "bg-green-50 border border-green-100 rounded-xl px-4 py-3 mt-4"
                        : "bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 mt-4"
                }
            >

                <p
                    className={
                        syncWithAccount
                            ? "text-[13px] text-green-700 font-semibold"
                            : "text-[13px] text-yellow-700 font-semibold"
                    }
                >

                    {syncWithAccount
                        ? "✓ Dữ liệu đang được lưu theo tài khoản"
                        : "Dữ liệu đang được lưu trên thiết bị này"
                    }

                </p>


                <p
                    className={
                        syncWithAccount
                            ? "text-[12px] text-green-600 mt-1"
                            : "text-[12px] text-yellow-600 mt-1"
                    }
                >

                    {syncWithAccount
                        ? "Có thể xem lại dữ liệu khi đăng nhập cùng tài khoản trên thiết bị khác."
                        : "Đăng nhập bằng tài khoản có mật khẩu để đồng bộ dữ liệu."
                    }

                </p>

            </div>


            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-5">


                <div className="lg:col-span-2 bg-white border rounded-2xl p-4 md:p-6">

                    <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                        Ghi dữ liệu mới

                    </h2>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">

                        {tool.fields.map(
                            (field) => (

                                <div
                                    key={field.key}
                                    className={
                                        field.type ===
                                        "textarea"
                                            ? "md:col-span-2"
                                            : ""
                                    }
                                >

                                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                                        {field.label}

                                    </label>


                                    {field.type ===
                                    "select" ? (

                                        <select
                                            value={
                                                form[
                                                    field.key
                                                ] ?? ""
                                            }
                                            onChange={
                                                (e) =>
                                                    updateField(
                                                        field.key,
                                                        e.target.value
                                                    )
                                            }
                                            className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-blue-500"
                                        >

                                            <option value="">
                                                Chọn...
                                            </option>


                                            {field.options.map(
                                                (option) => (

                                                    <option
                                                        key={option}
                                                        value={option}
                                                    >
                                                        {option}
                                                    </option>

                                                )
                                            )}

                                        </select>

                                    ) : field.type ===
                                      "textarea" ? (

                                        <textarea
                                            rows="5"
                                            value={
                                                form[
                                                    field.key
                                                ] ?? ""
                                            }
                                            placeholder={
                                                field.placeholder ||
                                                ""
                                            }
                                            onChange={
                                                (e) =>
                                                    updateField(
                                                        field.key,
                                                        e.target.value
                                                    )
                                            }
                                            className="w-full border rounded-xl p-3 outline-none focus:border-blue-500"
                                        />

                                    ) : (

                                        <div className="relative">

                                            <input
                                                type={
                                                    field.type
                                                }
                                                step="any"
                                                min={
                                                    field.min
                                                }
                                                max={
                                                    field.max
                                                }
                                                value={
                                                    form[
                                                        field.key
                                                    ] ?? ""
                                                }
                                                placeholder={
                                                    field.placeholder ||
                                                    "Nhập giá trị"
                                                }
                                                onChange={
                                                    (e) =>
                                                        updateField(
                                                            field.key,
                                                            e.target.value
                                                        )
                                                }
                                                className="w-full h-12 border rounded-xl px-3 pr-24 outline-none focus:border-blue-500"
                                            />


                                            {field.unit && (

                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">

                                                    {field.unit}

                                                </span>

                                            )}

                                        </div>

                                    )}

                                </div>

                            )
                        )}

                    </div>


                    <button
                        type="button"
                        disabled={saving}
                        onClick={handleSave}
                        className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl px-8 h-12 mt-5"
                    >

                        {saving
                            ? "Đang lưu..."
                            : "Lưu dữ liệu"
                        }

                    </button>

                </div>


                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 rounded-2xl p-5 h-fit">

                    <LineChartOutlined className="text-2xl text-blue-600" />


                    <p className="text-sm font-semibold text-gray-500 mt-3">

                        Dữ liệu hiện nhập

                    </p>


                    <p className="text-xl font-bold text-gray-900 mt-2 break-words">

                        {resultPreview ||
                            "Chưa nhập dữ liệu"
                        }

                    </p>


                    <p className="text-xs text-gray-500 leading-5 mt-3">

                        Kết quả ở đây chỉ giúp kiểm tra thông tin trước khi lưu.

                    </p>

                </div>

            </div>


            <div className="mt-8">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Lịch sử gần đây

                </h2>


                <p className="text-sm text-gray-500 mt-1">

                    Theo dõi sự thay đổi của dữ liệu đã ghi.

                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl p-8 text-center mt-4 text-gray-500">

                        Đang tải dữ liệu...

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl p-8 text-center mt-4">

                        <LineChartOutlined className="text-3xl text-gray-300" />

                        <p className="font-semibold text-gray-700 mt-3">

                            Chưa có dữ liệu

                        </p>

                    </div>

                ) : (

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">

                        {history.map(
                            (item) => (

                                <div
                                    key={item.id}
                                    className="bg-white border rounded-2xl p-4 flex justify-between gap-3"
                                >

                                    <div className="min-w-0">

                                        <p className="font-bold text-gray-900 break-words">

                                            {formatValue(
                                                tool,
                                                item
                                            )}

                                        </p>


                                        {formatExtra(
                                            tool,
                                            item
                                        ) && (

                                            <p className="text-[12px] text-gray-500 mt-1">

                                                {formatExtra(
                                                    tool,
                                                    item
                                                )}

                                            </p>

                                        )}


                                        <p className="text-[11px] text-gray-400 mt-2">

                                            {item.time &&
                                            item.date
                                                ? `${item.time} - ${item.date}`
                                                : item.created_at
                                                    ? new Date(
                                                        item.created_at
                                                    ).toLocaleString(
                                                        "vi-VN"
                                                    )
                                                    : ""
                                            }

                                        </p>

                                    </div>


                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleDelete(
                                                item.id
                                            )
                                        }
                                        className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"
                                        aria-label="Xóa"
                                    >

                                        <DeleteOutlined />

                                    </button>

                                </div>

                            )
                        )}

                    </div>

                )}

            </div>


            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-7">

                <div className="flex items-start">

                    <SafetyCertificateOutlined className="text-blue-600 text-xl mt-0.5" />


                    <div className="ml-3">

                        <p className="font-semibold text-gray-800 text-sm">

                            Lưu ý

                        </p>


                        <p className="text-[13px] text-gray-600 leading-5 mt-1">

                            Tiện ích dùng để ghi lại và theo dõi thông tin theo thời gian.
                            Kết quả không thay thế chẩn đoán, đánh giá hoặc tư vấn của nhân viên y tế.

                        </p>

                    </div>

                </div>

            </div>

        </div>
    );
}
